import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { IAppState } from '../../../core/store/store'
import { ITenantEditPropsState, TenantEditProps } from '../Tenants.types'
import { withPermissionsHOC } from '../../../core/hoc/permissionsHoc'
import { editTenant, fetchTenantById } from '../Tenants.actions'
import { IAccessScope, ITenant } from '../../../reducers/Reducers.interfaces'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import {
  Button,
  ButtonType,
  Form,
  FormFieldSize,
  FormGroup,
  Input,
  MultiSelect,
  Select,
  SubmitButton
} from '@digicert/dcone-common-ui'
import History from '../../../core/history/history'
import moment, { Moment } from 'moment';
import { translate } from '../../../shared/helpers/utils'
import { Translate } from 'react-localize-redux'
import NoPermissionsPage from '../../../shared/components/no-permissions-page'
import { NAME_REGEXP } from 'core/utils/constants'

const EditTenant = (props:TenantEditProps): ReactElement => {
  const dispatch: ThunkDispatch<IAppState, any, AnyAction> = useDispatch()
  const [tenant, setTenant] = useState<ITenant>()
  const [name, setName] = useState('')
  const [exactDate, setExactDate] = useState<Moment | null>(null)
  // set and update locales to support Select OptionProps
  const locales = useSelector((state: IAppState) => state.locales.locales.map(item => ({
    value: item.id,
    label: item.name
  })))
  const accessScope = useSelector((state: IAppState) => state.profile.data?.access_scope)
  const [selection, setSelection] = useState('')
  const validate = () => (!selection ? translate('form.validation.invalidSelect') as string: '')
  const [nameMaxLengthError, setNameMaxLengthError] = useState(false)
  const [applications, setApplications] = useState<any>([])
  const validateApp = () => (!applications ? translate('form.validation.invalidSelect') as string: '')
  const handleSelect = (value): void => {
    setSelection(value)
  }

  const getApplications = () => {
    const applications: Array<any> = []
    const apps = props.profile?.applications
    if (apps) {
      Object.keys(apps).reduce(
        (val, key) => {
          const application = {
            label: apps[key].name,
            value: apps[key].id,
          }
          applications.push(application)
          return application
        },
        {}
      )
    }

    applications?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return applications
  }

  const handleMultiManagerSelect = (value): void => {
    setApplications(value)
  }

  const changeName = (event: ChangeEvent<HTMLInputElement>) => {
    if (event) {
      const accountName = event.target.value
      setName(accountName)
        if (accountName.length > 255) {
          setNameMaxLengthError(true)
        } else {
          setNameMaxLengthError(false)
        }
    }

  }

  const nameValidity = useCallback(() => {
    if (nameMaxLengthError) {
      return translate('notifications.errors.maxLengthError') as string
    }
  }, [nameMaxLengthError])

  const onSubmit = (e) => {
    e.preventDefault()
    if (tenant) {
      const postData = {
        name: name,
        locale: selection,
        'service_period': {
          from: tenant.service_period.from,
          to: exactDate
        },
        applications: applications
      }
      dispatch(editTenant(tenant.id, postData, accessScope, ""))
    }
  }

  useEffect(() => {
    dispatch(fetchTenantById(props.match.params.id)).then((res: any) => {
      if (res) {
        setTenant(res)
        setExactDate(moment(res.service_period.to))
        setName(res.name)
        setSelection(locales.filter(item => item.value === res.locale)[0].value)
        setApplications(res.applications.map(app => {
          return app.id
        }))
      }
    })
  },[
    dispatch,
    props.match.params.id,
    setTenant,
    setExactDate,
    setName,
    setSelection
  ])

  if (accessScope === IAccessScope.account) {
    return (
      <NoPermissionsPage />
    )
  }

  else return (
    <>
      {tenant && (
        <section>
          <h1><Translate id='accounts.editAccount'/></h1>
          <Form
            center={false}
            onSubmit={(e => {onSubmit(e)})}
          >
            <FormGroup>
            <Input
              label={translate('createAccount.accountName') as string}
              size={FormFieldSize.M}
              required
              minLength={1}
              maxLength={256}
              value={name}
              onChange={changeName}
              visibilityToggle={true}
              errors={{
                valueMissing: translate('common.form.validation.required') as string
              }}
              id='account_name'
              validate={nameValidity}
            />
            <Input
              size={FormFieldSize.M}
              value={tenant.friendly_identifier}
              disabled
              label={translate('accounts.form.friendlyIdentifier') as string}
            />
            <p></p>
          </FormGroup>

            <FormGroup>
              <Select
                allowClear
                getPopupContainer={triggerNode => triggerNode.parentElement}
                validate={validate}
                defaultValue={selection}
                value={selection}
                onChange={handleSelect}
                options={locales}
                label={translate('profile.language') as string}
              />
            </FormGroup>

            <FormGroup>
              <MultiSelect
                allowClear
                getPopupContainer={triggerNode => triggerNode.parentElement}
                validate={validateApp}
                defaultValue={applications}
                value={applications}
                onChange={handleMultiManagerSelect}
                options={getApplications()}
                size={FormFieldSize.XXL}
                label={translate('common.applicationAccess') as string}
              />
            </FormGroup>


            <FormGroup colWidth={FormFieldSize.S}>
              <Button buttonType={ButtonType.SECONDARY} onClick={(e) => {
                e.preventDefault()
                History.goBack()
              }}>
                <Translate id='common.form.buttons.cancelBtn'/>
              </Button>

              <SubmitButton disabled={!name || !applications.length}>
                <Translate id='common.form.buttons.updateBtn'/>
              </SubmitButton>
            </FormGroup>
          </Form>
        </section>
      )}
    </>
  )
}
export default connect(
  (state: IAppState) : ITenantEditPropsState => ({
    localeList: state.locales,
    profile: state.profile?.data
  }),
  { fetchTenantById}
)(withPermissionsHOC(EditTenant, ['canManageAccount']))
